import ChatBubbleDisclosure from '../ChatBubbleDisclosure';
import { WorkflowConversationAction } from '../HomeChat/WorkflowConversationPresentation';
import { TextField } from '../FarChat/ChatFeedbackOverlays';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';

import Typography from '@mui/material/Typography';
import { ServicesContextConflicts } from '../ServicesQuestionAnswer';

import type {
  ConstructionSowArtifact,
  ConstructionSowArtifactRole,
  ConstructionSowNewUploadRole,
  ConstructionSowChatField,
  ConstructionSowChatSessionView,
  ConstructionSowChatTurnInput,
} from './types';
import HomeChatWorkflowBanner from '../HomeChat/HomeChatWorkflowBanner';
import ChatThread from '../FarChat/ChatThread';
import { stageFormGuidance } from '../HomeChat/stageFormGuidance';
import WorkflowIntakePrefillNotice from '../HomeChat/WorkflowIntakePrefillNotice';
import ResizableRequirementTextarea from '../ResizableRequirementTextarea';

const SUPPORT_ROLES: ReadonlyArray<{ role: ConstructionSowNewUploadRole; label: string }> = [
  { role: 'macc', label: 'MACC / MATOC' },
  { role: 'template', label: 'Template / reference' },
  { role: 'drawings', label: 'New Construction Drawings' },
  { role: 'as_built_drawings', label: 'As-Built Drawings' },
  { role: 'ae_guide', label: 'A/E guide' },
  { role: 'other', label: 'Other support document' },
];
const DEFAULT_PROJECT_CONTROL_IDS: readonly [string, string] = ['csow_project_number', 'csow_project_title'];
const RETIRED_CONTEXT_QUESTION_IDS = new Set(['csow_project_outcome']);
const RETIRED_CONTEXT_FIELDS = new Set(['project_outcome']);

function requestId(): string {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `construction-sow-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function text(value: unknown, fallback = ''): string {
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' ? String(value) : fallback;
}

function fieldKey(field: ConstructionSowChatField): string {
  return typeof field.id === 'string' && field.id ? field.id : typeof field.key === 'string' ? field.key : '';
}

function isActiveContextField(field: ConstructionSowChatField): boolean {
  return !RETIRED_CONTEXT_QUESTION_IDS.has(fieldKey(field))
    && !RETIRED_CONTEXT_FIELDS.has(text(field.field));
}

function activeAnswers(answers: Record<string, unknown>): Record<string, string | boolean | number> {
  return Object.entries(answers).reduce<Record<string, string | boolean | number>>((current, [key, value]) => {
    if (!RETIRED_CONTEXT_QUESTION_IDS.has(key) && (typeof value === 'string' || typeof value === 'boolean' || typeof value === 'number')) current[key] = value;
    return current;
  }, {});
}

function fieldLabel(field: ConstructionSowChatField): string {
  return text(field.question ?? field.label, fieldKey(field) || 'Context question');
}

function fieldRequired(field: ConstructionSowChatField): boolean {
  if (field.kind === 'proposal') return false;
  if (typeof field.required === 'boolean') return field.required;
  // The canonical construction context-question agent persists its priority
  // in lowercase (for example, `"high"`) and does not add `required`.  Keep
  // the existing explicit field authoritative, while treating its high-priority
  // contract exactly like the stage-205 workflow form treats it.
  if (typeof field.isRequired === 'boolean') return field.isRequired;
  if (typeof field.priority === 'string') {
    return ['high', 'required'].includes(field.priority.trim().toLowerCase());
  }
  return field.priority === 1 || field.priority === 2;
}

/** Keep question and answer numbering deterministic across the chat and stage. */
function requiredFieldsFirst(fields: readonly ConstructionSowChatField[]): ConstructionSowChatField[] {
  return fields
    .map((field, index) => ({ field, index }))
    .sort((left, right) => Number(fieldRequired(right.field)) - Number(fieldRequired(left.field)) || left.index - right.index)
    .map(({ field }) => field);
}

type SubmittedIntake = {
  requirement: string;
  files: Array<{ filename: string; label: string }>;
};

/**
 * A local copy of a completed answer turn while the server is deriving the
 * next round.  The durable read model replaces this after the mutation
 * returns; it exists only so a chat turn never disappears during that gap.
 */
type SubmittedQuestionRound = {
  round: 1 | 2;
  fields: readonly ConstructionSowChatField[];
  answers: Record<string, string | boolean | number>;
};

/**
 * A generation receipt and its transcript projection do not necessarily
 * arrive in the same response. Retain a submitted value until the durable
 * read model supplies a non-empty value for that key, so the user never sees
 * their just-submitted answer briefly change to "Not provided".
 */
function mergeCompletedAnswers(
  durableAnswers: Record<string, string | boolean | number>,
  submittedRound?: SubmittedQuestionRound | null,
): Record<string, string | boolean | number> {
  const merged = { ...(submittedRound?.answers || {}) };
  Object.entries(durableAnswers).forEach(([key, value]) => {
    if (typeof value === 'boolean' || typeof value === 'number' || (typeof value === 'string' && value.trim())) {
      merged[key] = value;
    }
  });
  return merged;
}

function submittedIntakeFrom(view: ConstructionSowChatSessionView): SubmittedIntake | null {
  const requirement = text(view.intake?.requirement ?? view.intake?.requirement_text).trim();
  const requirementDocument = text(view.intake?.requirement_document?.filename).trim();
  const artifacts = view.intake?.documents || view.intake?.artifacts || [];
  const roleLabel = (role: ConstructionSowArtifactRole) => role === 'requirements'
    ? 'Requirements package'
    : SUPPORT_ROLES.find((candidate) => candidate.role === role)?.label || 'Support document';
  const files = [
    ...(requirementDocument ? [{ filename: requirementDocument, label: 'Requirements package' }] : []),
    ...artifacts.map((artifact) => ({ filename: artifact.filename, label: roleLabel(artifact.role) })),
  ];
  return requirement || files.length ? { requirement, files } : null;
}

type Props = {
  initialView: ConstructionSowChatSessionView;
  onTurn: (input: ConstructionSowChatTurnInput) => Promise<ConstructionSowChatSessionView>;
  onUploadRequirementDocument: (file: File) => Promise<ConstructionSowChatSessionView>;
  onExtractRequirementDocument?: (confirmOverwrite: boolean) => Promise<ConstructionSowChatSessionView>;
  onUploadArtifact: (role: ConstructionSowNewUploadRole, file: File) => Promise<ConstructionSowChatSessionView>;
  onRemoveRequirementDocument?: () => Promise<ConstructionSowChatSessionView>;
  onReplaceArtifact?: (artifactId: string, file: File, role?: string) => Promise<ConstructionSowChatSessionView>;
  onRemoveArtifact?: (artifactId: string) => Promise<ConstructionSowChatSessionView>;
  onPollView?: (poll: number) => Promise<ConstructionSowChatSessionView>;
  onViewGeneratedSow?: () => Promise<void> | void;
  /** Home Chat's owner-scoped cancellation affordance, colocated with busy work. */
  processingAction?: React.ReactNode;
  /** Main Home Chat's launcher-owned utility, never supplied by a companion. */
  headerAction?: React.ReactNode;
  /** Display-only first constrained Home Chat message, retained across routing. */
  entryMessage?: string | null;
  /** Services reuses the established Construction chat presentation. */
  presentation?: {
    kind: 'services_pws';
    title: string;
    requirementLabel: string;
    requirementDocumentLabel: string;
    supportRoles: ReadonlyArray<{ role: string; label: string }>;
    projectControlIds: readonly [string, string];
    showProjectNumber?: boolean;
    requireProjectTitle?: boolean;
    documentControlsLabel: string;
    generatedDocumentLabel: string;
    handoffMessage: string;
    viewGeneratedLabel: string;
  };
};

/**
 * Thin chat projection of canonical construction stages 204–206.  The client
 * renders server-issued questions and artifact state, but never decides a
 * source, generates a document, or derives a stage/navigation target.
 */
const ConstructionSowChatPanel: React.FC<Props> = ({ initialView, onTurn, onUploadRequirementDocument, onExtractRequirementDocument, onUploadArtifact, onRemoveRequirementDocument, onReplaceArtifact, onRemoveArtifact, onPollView, onViewGeneratedSow, presentation, processingAction, headerAction, entryMessage = null }) => {
  const [view, setView] = useState(initialView);
  const [requirement, setRequirement] = useState(text(initialView.intake?.requirement ?? initialView.intake?.requirement_text));
  const [answers, setAnswers] = useState<Record<string, string | boolean | number>>({});
  const [busy, setBusy] = useState(false);
  const [submittedIntake, setSubmittedIntake] = useState<SubmittedIntake | null>(null);
  const [submittedQuestionRounds, setSubmittedQuestionRounds] = useState<Partial<Record<1 | 2, SubmittedQuestionRound>>>({});
  const [intakeSubmitting, setIntakeSubmitting] = useState(false);
  const [followUpSubmitting, setFollowUpSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progressRefreshFailed, setProgressRefreshFailed] = useState(false);
  const [refreshingProgress, setRefreshingProgress] = useState(false);
  const [openingDocument, setOpeningDocument] = useState(false);
  const pollHandlerRef = useRef(onPollView);
  const retryProgressRef = useRef<(() => void) | null>(null);
  const handoffInFlightRef = useRef(false);
  const previousServerErrorRef = useRef('');
  const appliedQuestionIdentity = useRef<string | null>(null);
  const requirementOwnership = useRef({ sessionId: initialView.session_id, edited: false });
  pollHandlerRef.current = onPollView;

  useEffect(() => setView(initialView), [initialView]);
  useEffect(() => {
    if (requirementOwnership.current.sessionId !== initialView.session_id) {
      requirementOwnership.current = { sessionId: initialView.session_id, edited: false };
    }
    // A late initial view or replay may hydrate an untouched form, but typing
    // (including clearing) gives this tab ownership until the session changes.
    if (!requirementOwnership.current.edited) {
      setRequirement(text(initialView.intake?.requirement ?? initialView.intake?.requirement_text));
    }
  }, [initialView.session_id, initialView.intake?.requirement, initialView.intake?.requirement_text]);
  // Authoring runs in a durable worker and terminal failures arrive through
  // polling rather than the initial click promise.  Promote the canonical,
  // safe server error into the same alert used for foreground form errors so
  // the Generate SOW button never reappears without an explanation.
  useEffect(() => {
    const serverError = text(view.error).trim();
    if (serverError) setError(serverError);
    else if (previousServerErrorRef.current) {
      const previous = previousServerErrorRef.current;
      setError((current) => current === previous ? null : current);
    }
    previousServerErrorRef.current = serverError;
  }, [view.error]);

  const stageKey = text(view.current_stage?.key);
  const cursor = text(view.cursor);
  const pending = view.pending_input;
  // Presence is the backend's versioned new-workflow marker. Its absence is
  // deliberate legacy compatibility, so never add a surprise question gate.
  const projectControlIds = presentation?.projectControlIds ?? DEFAULT_PROJECT_CONTROL_IDS;
  const showProjectNumber = presentation?.showProjectNumber !== false;
  const requireProjectTitle = presentation?.requireProjectTitle !== false;
  const projectDocumentControl = view.project_document_control && typeof view.project_document_control.version === 'number'
    ? view.project_document_control
    : null;
  const roundOneGenerating = view.question_rounds?.round_1?.status === 'generating' || view.questions?.status === 'generating';
  const roundTwoGenerating = view.question_rounds?.round_2?.status === 'generating';
  const followUpQuestionGenerationPending = followUpSubmitting || roundTwoGenerating;
  const questionGenerationPending = intakeSubmitting || roundOneGenerating || followUpQuestionGenerationPending;
  const generating = view.generation?.status === 'generating' || cursor === 'generating';
  const handoffReady = cursor === 'handoff_ready' || view.handoff?.status === 'handoff_ready' || view.actions?.some((action) => action.id === (presentation ? 'view_generated_pws' : 'view_generated_sow')) === true;
  const isIntake = cursor === 'intake' || stageKey === 'define_requirement' || (!pending && !generating && !handoffReady && !cursor);
  const serverQuestionFields = useMemo(
    () => {
      const active = (view.questions?.items || pending?.fields || []).filter(isActiveContextField);
      const saved = showProjectNumber ? [] : [...Object.values(view.fact_history || {}), ...Object.values(view.proposal_history || {})]
        .filter((field) => !active.some((current) => fieldKey(current) === fieldKey(field) || (current.field && current.field === field.field)))
        .map((field) => ({ ...field, saved_choice: true, required: false }));
      return [...active, ...saved].filter((field) => showProjectNumber || fieldKey(field) !== projectControlIds[0]);
    },
    [pending?.fields, view.questions?.items, projectControlIds, showProjectNumber, view.fact_history, view.proposal_history],
  );
  const questionFields = useMemo(() => serverQuestionFields
    // Fixed document-control inputs are intentionally not question cards.
    // Only the versioned marker enables this filtering so legacy transcripts
    // remain a lossless view of their historical server data.
    .filter((field) => !projectDocumentControl || !projectControlIds.includes(fieldKey(field))), [projectControlIds, projectDocumentControl, serverQuestionFields]);
  const orderedQuestionFields = useMemo(() => requiredFieldsFirst(questionFields), [questionFields]);
  const currentQuestionRound = Number(view.questions?.round);
  const roundOneFields = useMemo(
    () => requiredFieldsFirst((view.question_rounds?.round_1?.fields || (currentQuestionRound === 1 ? serverQuestionFields : [])).filter(isActiveContextField).filter((field) => showProjectNumber || fieldKey(field) !== projectControlIds[0])),
    [currentQuestionRound, serverQuestionFields, view.question_rounds?.round_1?.fields, projectControlIds, showProjectNumber],
  );
  const roundTwoFields = useMemo(
    () => requiredFieldsFirst((view.question_rounds?.round_2?.fields || (currentQuestionRound === 2 ? serverQuestionFields : [])).filter(isActiveContextField)),
    [currentQuestionRound, serverQuestionFields, view.question_rounds?.round_2?.fields],
  );
  const roundOneAnswers = activeAnswers(view.question_rounds?.round_1?.answers || (currentQuestionRound === 1 ? view.questions?.answers || {} : {}));
  const roundTwoAnswers = activeAnswers(view.question_rounds?.round_2?.answers || (currentQuestionRound === 2 ? view.questions?.answers || {} : {}));
  const optimisticRoundOne = submittedQuestionRounds[1] || null;
  const optimisticRoundTwo = submittedQuestionRounds[2] || null;
  const completedRoundOneFields = roundOneFields.length ? roundOneFields : optimisticRoundOne?.fields || [];
  const completedRoundOneAnswers = mergeCompletedAnswers(roundOneAnswers, optimisticRoundOne);
  const completedRoundTwoFields = roundTwoFields.length ? roundTwoFields : optimisticRoundTwo?.fields || [];
  const completedRoundTwoAnswers = mergeCompletedAnswers(roundTwoAnswers, optimisticRoundTwo);
  const roundOneSubmitted = ['round_2', 'ready_to_generate', 'generating', 'handoff_ready'].includes(cursor) || Boolean(optimisticRoundOne);
  const roundTwoSubmitted = ['ready_to_generate', 'generating', 'handoff_ready'].includes(cursor);
  const activeQuestionRound = cursor === 'round_1' ? 1 : cursor === 'round_2' ? 2 : null;
  const questionIdentity = `${cursor}:${text(view.questions?.round, text(pending?.kind))}:${questionFields.map(fieldKey).filter(Boolean).join('|')}`;
  const missing = [
    ...orderedQuestionFields.filter((field) => fieldRequired(field) && (!answers[fieldKey(field)] || String(answers[fieldKey(field)]).trim() === '')),
    ...(projectDocumentControl && activeQuestionRound === 1
      ? projectControlIds.filter((key, index) => (index === 0 ? showProjectNumber : requireProjectTitle) && !text(answers[key] ?? projectDocumentControl[index === 0 ? 'project_number' : 'project_title']).trim())
      : []),
  ];
  const artifacts = view.intake?.documents || view.intake?.artifacts || [];
  const hasRequirementDocument = Boolean(view.intake?.requirement_document);
  // Once intake has left the form, the cached stage-204 requirement is the
  // durable user turn. While the mutation is still in flight, retain the
  // client snapshot so the form never merely looks disabled.
  const visibleSubmittedIntake = submittedIntake || (!isIntake ? submittedIntakeFrom(view) : null);

  // Each server round is authoritative.  Resetting to just that round's
  // persisted answers prevents an R1 answer from leaking into the R2 payload.
  useEffect(() => {
    if (appliedQuestionIdentity.current === questionIdentity) return;
    appliedQuestionIdentity.current = questionIdentity;
    const persisted = view.questions?.answers || {};
    const next: Record<string, string | boolean | number> = {};
    questionFields.forEach((field) => {
      const key = fieldKey(field);
      const answer = persisted[key] ?? (field.saved_choice ? field.answer : undefined);
      if (key && (typeof answer === 'string' || typeof answer === 'boolean' || typeof answer === 'number')) next[key] = answer;
    });
    // Project controls belong exclusively to the Round 1 canonical contract.
    // Retaining them in local state for Round 2 makes an all-optional
    // follow-up look populated, but the stage-621/205 answer APIs correctly
    // reject those reserved Round 1 IDs as unknown Round 2 answers.
    if (projectDocumentControl && activeQuestionRound === 1) {
      const number = text(persisted[projectControlIds[0]] ?? projectDocumentControl.project_number);
      const title = text(persisted[projectControlIds[1]] ?? projectDocumentControl.project_title);
      if (showProjectNumber) next[projectControlIds[0]] = number;
      next[projectControlIds[1]] = title;
    }
    setAnswers(next);
  }, [activeQuestionRound, projectControlIds, projectDocumentControl, questionFields, questionIdentity, view.questions?.answers, showProjectNumber]);

  const progressPhase = generating ? 'draft' : roundTwoGenerating ? 'round_2' : roundOneGenerating ? 'round_1' : null;
  const canPollProgress = Boolean(onPollView);
  useEffect(() => {
    if (!progressPhase || !canPollProgress) {
      setProgressRefreshFailed(false);
      return undefined;
    }
    let cancelled = false;
    let inFlight = false;
    let failures = 0;
    let poll = 0;
    const tick = async () => {
      if (cancelled || inFlight) return;
      inFlight = true;
      setRefreshingProgress(true);
      try {
        const next = await pollHandlerRef.current!(++poll);
        if (!next || !next.intake || typeof next.intake !== 'object' || Array.isArray(next.intake)
          || !(text(next.cursor).trim() || text(next.current_stage?.key).trim())
          || next.session_id !== initialView.session_id
          || (next.home_chat_session_id && initialView.home_chat_session_id
            && next.home_chat_session_id !== initialView.home_chat_session_id)) {
          throw new Error('Invalid progress response');
        }
        if (!cancelled) {
          failures = 0;
          setProgressRefreshFailed(false);
          setView(next);
        }
      } catch {
        // Losing contact does not mean the durable authoring job failed.
        // Keep its last known state and retry only the status request.
        if (!cancelled && ++failures >= 3) setProgressRefreshFailed(true);
      } finally {
        inFlight = false;
        if (!cancelled) setRefreshingProgress(false);
      }
    };
    retryProgressRef.current = () => { void tick(); };
    const timer = setInterval(() => { void tick(); }, 1000);
    void tick();
    return () => {
      cancelled = true;
      retryProgressRef.current = null;
      clearInterval(timer);
    };
  }, [progressPhase, canPollProgress, initialView.home_chat_session_id, initialView.session_id]);

  const openGeneratedDocument = async () => {
    if (handoffInFlightRef.current) return;
    handoffInFlightRef.current = true;
    setOpeningDocument(true);
    setError(null);
    try {
      if (!onViewGeneratedSow) throw new Error('Document navigation is unavailable');
      await onViewGeneratedSow();
    } catch {
      setError(`Unable to open the generated ${presentation?.generatedDocumentLabel || 'SOW'}. Please try again.`);
    } finally {
      handoffInFlightRef.current = false;
      setOpeningDocument(false);
    }
  };

  const apply = useCallback(async (work: () => Promise<ConstructionSowChatSessionView>) => {
    setBusy(true);
    setError(null);
    try {
      const next = await work();
      setView(next);
      return next;
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : `Unable to save your ${presentation?.generatedDocumentLabel || 'construction SOW'} information.`);
      return undefined;
    } finally {
      setBusy(false);
    }
  }, []);

  const upload = useCallback((role: ConstructionSowNewUploadRole | 'requirements' | string, file: File | undefined) => {
    if (!file) return;
    void apply(() => role === 'requirements'
      ? onUploadRequirementDocument(file)
      : onUploadArtifact(role as ConstructionSowNewUploadRole, file));
  }, [apply, onUploadArtifact, onUploadRequirementDocument]);

  const submitIntake = useCallback(() => {
    if (!requirement.trim()) return;
    const snapshot: SubmittedIntake = {
      requirement: requirement.trim(),
      files: (submittedIntakeFrom(view)?.files || []),
    };
    setSubmittedIntake(snapshot);
    setIntakeSubmitting(true);
    void apply(() => onTurn({ client_request_id: requestId(), action: 'submit_intake', input: { requirement: snapshot.requirement } }))
      .finally(() => setIntakeSubmitting(false));
  }, [apply, onTurn, requirement, view]);

  const extractRequirement = useCallback(() => {
    if (!onExtractRequirementDocument) return;
    const confirmOverwrite = Boolean(requirement.trim());
    if (confirmOverwrite && !window.confirm(`Replace the typed ${presentation?.requirementLabel || 'construction requirement'} with text extracted from the ${presentation?.requirementDocumentLabel || 'requirements package'}?`)) return;
    void apply(async () => {
      const next = await onExtractRequirementDocument(confirmOverwrite);
      requirementOwnership.current.edited = true;
      setRequirement(text(next.intake?.requirement ?? next.intake?.requirement_text));
      return next;
    });
  }, [apply, onExtractRequirementDocument, requirement]);

  const removeRequirementDocument = useCallback(() => {
    if (!onRemoveRequirementDocument) return;
    void apply(onRemoveRequirementDocument);
  }, [apply, onRemoveRequirementDocument]);

  const replaceArtifact = useCallback((artifactId: string, file: File | undefined, role?: string) => {
    if (!file || !onReplaceArtifact) return;
    // Construction's established controller takes two arguments.  The shared
    // Services presentation additionally needs its server-owned role, so only
    // provide that third argument when the Services façade supplied one.
    void apply(() => role === undefined
      ? onReplaceArtifact(artifactId, file)
      : onReplaceArtifact(artifactId, file, role));
  }, [apply, onReplaceArtifact]);

  const removeArtifact = useCallback((artifactId: string) => {
    if (!onRemoveArtifact) return;
    void apply(() => onRemoveArtifact(artifactId));
  }, [apply, onRemoveArtifact]);

  const submitQuestions = useCallback(() => {
    if (missing.length) return;
    const finalRound = cursor === 'round_2';
    // Defend the request boundary as well as the state reset above. This
    // covers a quick round transition before React's effect has reset local
    // answers, and keeps both Construction and Services (which share this
    // component) aligned with their per-round canonical ID contracts.
    const submittedAnswers = finalRound
      ? Object.keys(answers).reduce<Record<string, string | boolean | number>>((current, key) => {
        if (!projectControlIds.includes(key)) current[key] = answers[key];
        return current;
      }, {})
      : activeAnswers(answers);
    const submittedRound = finalRound ? 2 : 1;
    setSubmittedQuestionRounds((current) => ({
      ...current,
      [submittedRound]: {
        round: submittedRound,
        fields: submittedRound === 1 ? (roundOneFields.length ? roundOneFields : orderedQuestionFields) : (roundTwoFields.length ? roundTwoFields : orderedQuestionFields),
        answers: { ...submittedAnswers },
      },
    }));
    if (!finalRound) {
      setFollowUpSubmitting(true);
    }
    void apply(() => onTurn({
      client_request_id: requestId(),
      action: finalRound ? 'save_answers_and_generate' : 'save_answers',
      input: { answers: submittedAnswers },
    })).then((next) => {
      if (!finalRound) {
        setFollowUpSubmitting(false);
        // Do not leave a successful-looking local transcript behind when the
        // save fails. On success it remains only as a fallback for incomplete
        // legacy read models; durable `question_rounds.round_1` wins.
        if (!next) setSubmittedQuestionRounds((current) => ({ ...current, 1: undefined }));
      } else if (!next) {
        setSubmittedQuestionRounds((current) => ({ ...current, 2: undefined }));
      }
    });
  }, [answers, apply, cursor, missing.length, onTurn, orderedQuestionFields, projectControlIds, roundOneFields, roundTwoFields]);

  const generate = useCallback(() => {
    if (missing.length) return;
    setSubmittedQuestionRounds((current) => ({
      ...current,
      2: {
        round: 2,
        fields: roundTwoFields,
        answers: activeAnswers(answers),
      },
    }));
    void apply(() => onTurn({ client_request_id: requestId(), action: 'generate', input: { answers } })).then((next) => {
      if (!next) setSubmittedQuestionRounds((current) => ({ ...current, 2: undefined }));
    });
  }, [answers, apply, missing.length, onTurn, roundTwoFields]);

  // `presentation` reuses this proven chat surface for Services PWS uploads.
  // Its artifact roles are server-defined strings rather than the Construction
  // union, so compare the safe read-model values as strings at this boundary.
  const artifactsFor = (role: string): ConstructionSowArtifact[] => artifacts.filter((artifact) => String(artifact.role) === role);
  const renderRequirementMarker = (field: ConstructionSowChatField, number: number) => {
    const required = fieldRequired(field);
    return <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
      <Chip label={number} size="small" color={required ? 'error' : 'success'} sx={{ fontWeight: 'bold' }} />
      <Typography variant="body2" color={required ? 'error' : 'success.main'} sx={{ fontWeight: 600 }}>
        {required ? '* Required' : 'Optional'}
      </Typography>
    </Stack>;
  };
  // Completed turns should read like ordinary chat history. The active form
  // retains the full Services-style Required/Optional marker; history only
  // needs the compact, accessible required indicator.
  const renderCompletedRequirementMarker = (field: ConstructionSowChatField) => fieldRequired(field)
    ? <Box component="span" role="img" aria-label="Required question" sx={{ color: 'error.main', fontWeight: 700, mr: 0.5 }}>*</Box>
    : null;
  const renderCompletedQuestionRound = (round: 1 | 2, fields: readonly ConstructionSowChatField[], roundAnswers: Record<string, unknown>) => {
    if (!fields.length) return null;
    const heading = round === 2 ? 'Follow-up Questions' : 'Context Questions';
    return <React.Fragment key={`${view.session_id}:completed-round-${round}`}>
      <Paper
        component="section"
        elevation={0}
        data-role="assistant"
        data-testid={`construction-sow-chat-round-${round}-questions`}
        sx={{ alignSelf: 'flex-start', maxWidth: 'min(100%, 680px)', px: 2, py: 1.5, bgcolor: 'action.hover', color: 'text.primary', borderRadius: 2 }}
      >
        <ChatBubbleDisclosure alwaysCollapsible label={`${round === 2 ? 'follow-up' : 'context'} questions`} preview="" title={<Typography variant="overline" sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.4 }}>{heading}</Typography>}>
        <Box component="ol" sx={{ my: 0, pl: 3 }}>
          {fields.map((field) => <Box component="li" key={fieldKey(field)} sx={{ py: 0.4 }}>
            <Typography>{renderCompletedRequirementMarker(field)}{fieldLabel(field)}</Typography>
          </Box>)}
        </Box>
        </ChatBubbleDisclosure>
      </Paper>
      <Paper
        component="section"
        elevation={0}
        data-role="user"
        data-testid={`construction-sow-chat-round-${round}-answers`}
        sx={{ alignSelf: 'flex-end', maxWidth: 'min(100%, 680px)', px: 2, py: 1.5, bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: 2 }}
      >
        <ChatBubbleDisclosure alwaysCollapsible label={`${round === 2 ? 'follow-up' : 'context'} answers`} preview="" title={<Typography variant="overline" sx={{ display: 'block', opacity: 0.82, lineHeight: 1.4 }}>Answers</Typography>}>
        <Box component="ol" sx={{ my: 0, pl: 3 }}>
          {fields.map((field) => {
            const value = roundAnswers[fieldKey(field)];
            return <Box component="li" key={fieldKey(field)} sx={{ py: 0.4, whiteSpace: 'pre-wrap' }}>
              {value === undefined || value === null || String(value).trim() === '' ? 'Not provided' : text(value)}
            </Box>;
          })}
        </Box>
        </ChatBubbleDisclosure>
      </Paper>
    </React.Fragment>;
  };
  const renderQuestionFields = (fields: readonly ConstructionSowChatField[]) => fields.map((field, index) => {
              const key = fieldKey(field);
              const label = fieldLabel(field);
              const required = fieldRequired(field);
              const inputType = field.kind === 'proposal' ? 'yes_no' : field.input_type || 'textarea';
              const questionId = `construction-sow-question-${key}`;
              const question = <Box><Box component="span" data-testid={`construction-sow-question-marker-${key}`}>{renderRequirementMarker(field, index + 1)}</Box><Typography id={questionId} data-testid={questionId} variant="subtitle1" sx={{ fontSize: '1.0625rem', fontWeight: 700, lineHeight: 1.45 }}>
                {label}{required && <Box component="span" data-testid={`construction-sow-question-required-${key}`} aria-label="Required field" sx={{ display: 'none' }}>* Required</Box>}
              </Typography></Box>;
              if (inputType === 'radio' || inputType === 'select' || inputType === 'yes_no') {
                const opts = (inputType === 'yes_no' ? ['Yes', 'No'] : field.options || []).map((option) => typeof option === 'string' ? { value: option, label: option } : { value: text(option.value), label: text(option.label ?? option.value) });
                return inputType !== 'select'
                  ? <FormControl key={key} required={required}>{question}<RadioGroup aria-labelledby={questionId} value={text(answers[key])} onChange={(event) => setAnswers((current) => ({ ...current, [key]: event.target.value }))}>{opts.map((option) => <FormControlLabel key={option.value} value={option.value} control={<Radio />} label={option.label} />)}</RadioGroup>{inputType === 'yes_no' && answers[key] && <Button size="small" onClick={() => setAnswers((current) => ({ ...current, [key]: '' }))}>Clear choice</Button>}{inputType === 'yes_no' && <Typography variant="caption" color="text.secondary">Optional. Unanswered additions will not be included.</Typography>}</FormControl>
                  : <Stack key={key} spacing={0.75}>{question}<TextField select fullWidth value={text(answers[key])} required={required} SelectProps={{ inputProps: { 'aria-label': `Answer: ${label}` } }} onChange={(event) => setAnswers((current) => ({ ...current, [key]: event.target.value }))}>{opts.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}</TextField></Stack>;
              }
              return <Stack key={key} spacing={0.75}>{question}<TextField value={text(answers[key])} required={required} multiline={inputType === 'textarea'} minRows={inputType === 'textarea' ? 3 : undefined} fullWidth inputProps={{ 'aria-label': `Answer: ${label}` }} helperText={text(field.rationale ?? field.help_text)} onChange={(event) => setAnswers((current) => ({ ...current, [key]: event.target.value }))} /></Stack>;
            });
  const renderProjectDocumentControls = () => {
    if (!projectDocumentControl || activeQuestionRound !== 1) return null;
    const controls: Array<{ key: string; label: string; control: 'project_number' | 'project_title' }> = [
      { key: projectControlIds[0], label: 'Project Number', control: 'project_number' },
      { key: projectControlIds[1], label: 'Project Title', control: 'project_title' },
    ];
    return <Box data-testid={`${presentation ? 'services-pws' : 'construction-sow'}-chat-project-document-controls`} sx={{ mb: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>{presentation?.documentControlsLabel || 'SOW document controls'}</Typography>
      <Stack spacing={1.25}>
        {controls.filter(({ control }) => showProjectNumber || control !== 'project_number').map(({ key, label, control }) => <Box key={key}>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
            <Chip label={control === 'project_title' && !requireProjectTitle ? 'Optional' : 'Required'} size="small" color={control === 'project_title' && !requireProjectTitle ? 'success' : 'error'} />
            <Typography id={`${key}-question`} variant="body1" sx={{ fontWeight: 600 }}>{label}</Typography>
          </Stack>
          <TextField
            required={control !== 'project_title' || requireProjectTitle}
            helperText={control === 'project_title' && !requireProjectTitle ? 'Leave blank to generate a title from your service requirement.' : undefined}
            fullWidth
            value={text(answers[key] ?? projectDocumentControl[control])}
            onChange={(event) => setAnswers((current) => ({ ...current, [key]: event.target.value }))}
            inputProps={{ 'aria-labelledby': `${key}-question` }}
          />
        </Box>)}
      </Stack>
    </Box>;
  };
  const renderUpload = (role: ConstructionSowNewUploadRole | 'requirements' | string, label: string) => (
    <Box key={role}>
      <Button component="label" variant="outlined" fullWidth disabled={busy}>
        {role === 'requirements' && hasRequirementDocument ? 'Replace' : 'Upload'} {label}
        <input hidden type="file" onChange={(event) => upload(role, event.target.files?.[0])} />
      </Button>
      {artifactsFor(role).map((artifact) => <Stack key={artifact.artifact_id} direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>{artifact.filename}{text(artifact.analysis_status ?? artifact.status) ? ` — ${text(artifact.analysis_status ?? artifact.status)}` : ''}</Typography>
        {onReplaceArtifact && <Button component="label" size="small" variant="text" disabled={busy}>Replace<input hidden type="file" aria-label={`Replace ${artifact.filename}`} onChange={(event) => replaceArtifact(artifact.artifact_id, event.target.files?.[0], presentation ? artifact.role : undefined)} /></Button>}
        {onRemoveArtifact && <Button size="small" color="error" variant="text" aria-label={`Remove ${artifact.filename}`} disabled={busy} onClick={() => removeArtifact(artifact.artifact_id)}>Remove</Button>}
      </Stack>)}
    </Box>
  );


  const showPendingForm = !generating && !questionGenerationPending && !handoffReady && !isIntake
    && (pending || cursor === 'round_1' || cursor === 'round_2' || cursor === 'ready_to_generate');
  const pendingStageTitle = activeQuestionRound === 2 ? 'Follow-up Questions'
    : activeQuestionRound === 1 ? 'Context Questions'
    : text(view.current_stage?.label, `Generate ${presentation?.generatedDocumentLabel || 'SOW'}`);
  const pendingGuidance = stageFormGuidance(pending?.prompt, view.assistant_message).filter(copy => copy !== pendingStageTitle);

  return (
    <Paper square elevation={0} sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }} data-testid={presentation ? 'services-pws-chat-panel' : 'construction-sow-chat-panel'}>
      <HomeChatWorkflowBanner
        title={presentation?.title || 'Construction SOW'}
        subtitle={presentation
          ? 'Build and review the service performance work statement.'
          : 'Build and review the construction statement of work.'}
        action={headerAction}
      />
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mx: { xs: 2, sm: 3 }, mt: 1.5 }}>{error}</Alert>}
      {progressRefreshFailed && <Alert severity="warning" sx={{ mx: { xs: 2, sm: 3 }, mt: 1.5 }} action={<Button color="inherit" size="small" disabled={refreshingProgress} onClick={() => retryProgressRef.current?.()}>Retry status</Button>}>Unable to refresh progress. The last saved status is still shown.</Alert>}
      <Box aria-busy={busy || generating || questionGenerationPending} sx={{ overflowY: 'auto', flex: 1, minHeight: 0, px: { xs: 2, sm: 3 }, py: 1.5, pr: { xs: 2.5, sm: 3.5 } }}>
        <Stack spacing={1.5}>
          <WorkflowConversationAction />
          {entryMessage?.trim() && (
            <ChatThread
              compact
              messages={[{ role: 'user', content: entryMessage }]}
              emptyPlaceholder={null}
            />
          )}
          {!showPendingForm && text(view.assistant_message) && <Typography>{text(view.assistant_message)}</Typography>}
          {visibleSubmittedIntake && <Paper
            component="section"
            elevation={0}
            data-testid="construction-sow-chat-submitted-intake"
            sx={{ alignSelf: 'flex-end', maxWidth: 'min(100%, 680px)', px: 2, py: 1.5, bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: 2 }}
          >
            <ChatBubbleDisclosure key={`${view.session_id}:submitted-intake`} alwaysCollapsible label="submitted workflow intake" preview="" title={<Typography variant="overline" sx={{ display: 'block', opacity: 0.82, lineHeight: 1.4 }}>{presentation?.requirementLabel || 'Construction requirement'}</Typography>}>
            {visibleSubmittedIntake.requirement && <Typography sx={{ whiteSpace: 'pre-wrap' }}>{visibleSubmittedIntake.requirement}</Typography>}
            {visibleSubmittedIntake.files.length > 0 && <Stack spacing={0.25} sx={{ mt: visibleSubmittedIntake.requirement ? 1 : 0 }}>
              {visibleSubmittedIntake.files.map((file) => <Typography key={`${file.label}:${file.filename}`} variant="caption" sx={{ display: 'block', opacity: 0.9 }}>{file.label}: {file.filename}</Typography>)}
            </Stack>}
            </ChatBubbleDisclosure>
          </Paper>}
          {roundOneSubmitted && renderCompletedQuestionRound(1, completedRoundOneFields, completedRoundOneAnswers)}
          {roundTwoSubmitted && renderCompletedQuestionRound(2, completedRoundTwoFields, completedRoundTwoAnswers)}
          {(intakeSubmitting || roundOneGenerating) && <Stack direction="row" spacing={1} alignItems="center" role="status" data-testid="construction-sow-chat-question-generation"><CircularProgress size={18} /><Typography variant="body2">Generating Context Questions.</Typography>{processingAction}</Stack>}
          {followUpQuestionGenerationPending && <Paper component="section" elevation={0} role="status" data-testid="construction-sow-chat-follow-up-generation" sx={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25, bgcolor: 'action.hover', color: 'text.primary', borderRadius: 2 }}><CircularProgress size={18} /><Typography variant="body2">Generating follow-up questions</Typography>{processingAction}</Paper>}
          {generating && <Stack alignItems="center" spacing={1.25} role="status" data-testid={presentation ? 'services-pws-chat-generation-progress' : 'construction-sow-chat-generation-progress'} sx={{ py: 5 }}><CircularProgress /><Typography>{text(view.generation?.message ?? view.progress?.message, `Generating your ${presentation?.generatedDocumentLabel || 'SOW'}.`)}</Typography>{processingAction}</Stack>}
          {!generating && !questionGenerationPending && handoffReady && <Box data-testid={presentation ? 'services-pws-chat-handoff' : 'construction-sow-chat-handoff'}>{presentation && text(projectDocumentControl?.project_title).trim() && <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>{text(projectDocumentControl?.project_title)}</Typography>}<Alert severity="success" icon={false}>{presentation?.handoffMessage || 'Your generated SOW is ready to review and edit in One-time Construction.'}</Alert><Button variant="contained" sx={{ mt: 1.5 }} disabled={openingDocument} onClick={() => void openGeneratedDocument()}>{presentation?.viewGeneratedLabel || 'View generated SOW'}</Button></Box>}
          {!generating && !questionGenerationPending && !handoffReady && isIntake && <Box component="form" noValidate onSubmit={(event) => { event.preventDefault(); submitIntake(); }}>
            <ChatBubbleDisclosure key={`${view.session_id}:intake`} alwaysCollapsible defaultExpanded label="workflow intake" preview="" title={<Typography variant="subtitle1" fontWeight={700}>{presentation?.requirementLabel || 'Construction requirement'}</Typography>}>
            <Stack spacing={1.5}>
              {renderUpload('requirements', presentation?.requirementDocumentLabel || 'Requirements Package')}
              {hasRequirementDocument && onRemoveRequirementDocument && <Button variant="text" color="error" size="small" sx={{ alignSelf: 'flex-start', mt: -1 }} disabled={busy} onClick={removeRequirementDocument}>Remove {presentation?.requirementDocumentLabel?.toLowerCase() || 'requirements package'}</Button>}
              {onExtractRequirementDocument && <Button variant="text" size="small" sx={{ alignSelf: 'flex-start' }} disabled={busy || !hasRequirementDocument} onClick={extractRequirement}>Auto-fill requirement from document</Button>}
              <WorkflowIntakePrefillNotice prefill={view.prefill} />
              <ResizableRequirementTextarea label={presentation?.requirementLabel || 'Construction requirement'} resizeKey={`${presentation?.kind || 'construction-sow'}:${view.session_id}:requirement`} value={requirement} onChange={(event) => { requirementOwnership.current.edited = true; setRequirement(event.target.value); }} required helperText={`Describe the ${presentation?.requirementLabel?.toLowerCase() || 'construction requirement'}. You may upload a requirements package separately.`} />
              <Typography variant="subtitle2">Supporting documents</Typography>
              <Stack spacing={1}>{(presentation?.supportRoles || SUPPORT_ROLES).map(({ role, label }) => renderUpload(role, label))}</Stack>
              <Button type="submit" variant="contained" disabled={busy || !requirement.trim()}>Continue to Context Questions</Button>
            </Stack>
            </ChatBubbleDisclosure>
          </Box>}
          {showPendingForm && <Box component="form" noValidate onSubmit={(event) => { event.preventDefault(); cursor === 'ready_to_generate' || pending?.kind === 'generate_sow' || pending?.kind === 'generate_pws' ? generate() : submitQuestions(); }} sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2 }}>
            <ChatBubbleDisclosure key={`${view.session_id}:${questionIdentity}`} alwaysCollapsible={activeQuestionRound !== null} forceOpen={activeQuestionRound === null} defaultExpanded label={activeQuestionRound === 2 ? 'follow-up questions' : 'context questions'} preview="" title={<Typography component="h3" variant="h6" sx={{ fontWeight: 700 }}>{pendingStageTitle}</Typography>}>
            {pendingGuidance.map(copy => <Typography key={copy} variant="body2" sx={{ mt: 0.5 }}>{copy}</Typography>)}
            {presentation?.kind !== 'services_pws' && renderProjectDocumentControls()}
            {presentation?.kind === 'services_pws' && <ServicesContextConflicts conflicts={view.context_conflicts} />}
            {activeQuestionRound !== null && <Stack spacing={1.25} sx={{ mt: 1.5 }} data-testid="construction-sow-chat-active-questions">{renderQuestionFields(orderedQuestionFields.filter((field) => !field.saved_choice))}</Stack>}
            {orderedQuestionFields.some((field) => field.saved_choice) && <Box component="details" sx={{ mt: 2 }}><Typography component="summary" sx={{ cursor: 'pointer' }}>Saved choices</Typography><Typography variant="body2" color="text.secondary">These answers are already saved. Change them only if needed.</Typography>{renderQuestionFields(orderedQuestionFields.filter((field) => field.saved_choice))}</Box>}
            {presentation?.kind === 'services_pws' && renderProjectDocumentControls()}
            <Button type="submit" variant="contained" sx={{ mt: 2 }} disabled={busy || missing.length > 0}>{cursor === 'round_2' || cursor === 'ready_to_generate' || pending?.kind === 'generate_sow' || pending?.kind === 'generate_pws' ? `Generate ${presentation?.generatedDocumentLabel || 'SOW'}` : 'Save and continue'}</Button>
            </ChatBubbleDisclosure>
          </Box>}
        </Stack>
      </Box>
    </Paper>
  );
};

export default ConstructionSowChatPanel;
